/**
 * traces export — real SDK implementation
 *
 * Uses client.listTraces() to fetch all traces, then formats as JSON or CSV.
 * Outputs to stdout by default, or to a file with --output.
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { handleError } from '../../lib/errors.js';
import type { Trace } from '../../types/index.js';

interface ExportOptions {
  prompt?: string;
  format: string;
  output?: string;
}

export function registerExportCommand(parent: Command): void {
  parent
    .command('export')
    .description('Export traces')
    .option('-p, --prompt <id>', 'Filter by prompt ID')
    .option('-f, --format <format>', 'Export format (json, csv)', 'json')
    .option('-o, --output <path>', 'Output file path')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent traces export
  ${chalk.dim('$')} mutagent traces export --format json --output traces.json
  ${chalk.dim('$')} mutagent traces export --format csv --output traces.csv
  ${chalk.dim('$')} mutagent traces export --prompt <prompt-id> --format json

${chalk.dim('Exports to stdout by default. Use --output to save to a file.')}
    `)
    .action(async (options: ExportOptions) => {
      const isJson = getJsonFlag(parent);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const tracesList = await client.listTraces({
          promptId: options.prompt,
        });

        let content: string;

        if (options.format === 'json') {
          content = JSON.stringify(tracesList, null, 2);
        } else if (options.format === 'csv') {
          const headers = ['id', 'promptId', 'latency', 'tokensInput', 'tokensOutput', 'timestamp'];
          const rows = (tracesList as Trace[]).map((t) => [
            t.id,
            t.promptId,
            t.latency,
            t.tokens.input,
            t.tokens.output,
            t.timestamp,
          ]);
          content = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        } else {
          throw new Error(`Unsupported format: ${options.format}`);
        }

        if (options.output) {
          const { writeFileSync } = await import('fs');
          writeFileSync(options.output, content);
          output.success(`Exported ${String(tracesList.length)} traces to ${options.output}`);
        } else {
          process.stdout.write(content);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });
}
